import type { RawComponent } from "../types.js";

interface UaBrandVersion {
  brand: string;
  version: string;
}

interface NavigatorUAHighEntropyValues {
  architecture?: string;
  bitness?: string;
  formFactors?: string[];
  fullVersionList?: UaBrandVersion[];
  model?: string;
  platformVersion?: string;
  wow64?: boolean;
}

interface NavigatorUAData {
  brands: UaBrandVersion[];
  mobile: boolean;
  platform: string;
  getHighEntropyValues?: (hints: string[]) => Promise<NavigatorUAHighEntropyValues>;
}

/**
 * Human-readable browser/device metadata shown to moderators. It is submitted
 * separately from fingerprint components so these fields never silently change
 * similarity weights or the combined fingerprint hash.
 */
export interface DeviceProfile {
  browserFamily: string | null;
  browserFullVersion: string | null;
  osPlatform: string | null;
  osVersion: string | null;
  deviceModel: string | null;
  formFactors: string[];
  architecture: string | null;
  bitness: string | null;
  wow64: boolean | null;
  mobile: boolean | null;
  languages: string | null;
  locale: string | null;
  timezone: string | null;
  calendar: string | null;
  numberingSystem: string | null;
  hourCycle: string | null;
  screenResolution: string | null;
  pixelRatio: number | null;
  touchSupport: boolean | null;
  pointerCapabilities: string | null;
  hardwareConcurrency: number | null;
  deviceMemoryGb: number | null;
  webglVendor: string | null;
  webglRenderer: string | null;
}

function parseBrowserFamilyVersion(ua: string): { family: string; version: string } {
  const patterns: Array<[string, RegExp]> = [
    ["Edge", /Edg\/([\d.]+)/],
    ["Opera", /OPR\/([\d.]+)/],
    ["Chrome", /Chrome\/([\d.]+)/],
    ["Firefox", /Firefox\/([\d.]+)/],
    ["Safari", /Version\/([\d.]+).*Safari/],
  ];
  for (const [family, re] of patterns) {
    const match = re.exec(ua);
    if (match) return { family, version: match[1] ?? "" };
  }
  return { family: "unknown", version: "" };
}

function fullVersionForBrowser(
  family: string,
  list: UaBrandVersion[] | undefined,
): string | null {
  if (!list?.length) return null;
  const needles: Record<string, string[]> = {
    Chrome: ["Google Chrome", "Chromium"],
    Edge: ["Microsoft Edge", "Edge"],
    Opera: ["Opera"],
    Firefox: ["Firefox"],
    Safari: ["Safari"],
  };
  const candidates = needles[family] ?? [family];
  const match = list.find((entry) => candidates.some((needle) => entry.brand.includes(needle)));
  return match?.version || null;
}

function parseFallbackOsVersion(ua: string): string | null {
  const android = /Android\s+([\d.]+)/i.exec(ua)?.[1];
  if (android) return android;
  const ios = /(?:CPU (?:iPhone )?OS|iPhone OS)\s+([\d_]+)/i.exec(ua)?.[1];
  if (ios) return ios.replaceAll("_", ".");
  const mac = /Mac OS X\s+([\d_]+)/i.exec(ua)?.[1];
  if (mac) return mac.replaceAll("_", ".");
  const windowsNt = /Windows NT\s+([\d.]+)/i.exec(ua)?.[1];
  if (windowsNt) return `NT ${windowsNt}`;
  return null;
}

function parseFallbackDeviceModel(ua: string): string | null {
  const androidBuild = /Android[^;)]*;\s*([^;)]*?)\s+Build\//i.exec(ua)?.[1]?.trim();
  if (androidBuild) return androidBuild;
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  return null;
}

function componentValue(components: RawComponent[], name: RawComponent["name"]): string | null {
  return components.find((component) => component.name === name)?.rawValue ?? null;
}

function numberValue(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Collects moderator-facing descriptive metadata. High-entropy UA Client Hints
 * are optional and never gate verification; unsupported or restricted browsers
 * simply return fewer details. These values remain attacker-controlled browser
 * claims and are not used as trusted identity evidence.
 */
export async function collectDeviceProfile(components: RawComponent[]): Promise<DeviceProfile> {
  const nav = navigator as Navigator & { userAgentData?: NavigatorUAData };
  const { family, version } = parseBrowserFamilyVersion(nav.userAgent);

  let highEntropy: NavigatorUAHighEntropyValues = {};
  const getHighEntropyValues = nav.userAgentData?.getHighEntropyValues?.bind(nav.userAgentData);
  if (getHighEntropyValues) {
    try {
      highEntropy = await getHighEntropyValues([
        "architecture",
        "bitness",
        "fullVersionList",
        "model",
        "platformVersion",
        "wow64",
      ]);
    } catch {
      highEntropy = {};
    }
    // formFactors is newer than the other hints; query it independently so a
    // browser that does not recognize it cannot discard the useful base set.
    try {
      const formFactorResult = await getHighEntropyValues(["formFactors"]);
      if (formFactorResult.formFactors) highEntropy.formFactors = formFactorResult.formFactors;
    } catch {
      // Optional metadata only.
    }
  }

  let intl: {
    locale?: string;
    calendar?: string;
    numberingSystem?: string;
    hourCycle?: string;
  } = {};
  try {
    intl = Intl.DateTimeFormat().resolvedOptions() as typeof intl;
  } catch {
    intl = {};
  }

  const mobile =
    nav.userAgentData?.mobile ?? /(?:Android|iPhone|iPad|iPod|Mobile)/i.test(nav.userAgent);
  const suppliedFormFactors = highEntropy.formFactors?.filter((value) => value.length > 0) ?? [];
  const touchValue = componentValue(components, "touch_support");

  return {
    browserFamily: family === "unknown" ? null : family,
    browserFullVersion: fullVersionForBrowser(family, highEntropy.fullVersionList) || version || null,
    osPlatform: componentValue(components, "os_platform"),
    osVersion: highEntropy.platformVersion || parseFallbackOsVersion(nav.userAgent),
    deviceModel: highEntropy.model || parseFallbackDeviceModel(nav.userAgent),
    formFactors: suppliedFormFactors.length > 0 ? suppliedFormFactors : [mobile ? "Mobile" : "Desktop"],
    architecture: highEntropy.architecture || null,
    bitness: highEntropy.bitness || null,
    wow64: highEntropy.wow64 ?? null,
    mobile,
    languages: componentValue(components, "languages"),
    locale: intl.locale ?? nav.language ?? null,
    timezone: componentValue(components, "timezone"),
    calendar: intl.calendar ?? null,
    numberingSystem: intl.numberingSystem ?? null,
    hourCycle: intl.hourCycle ?? null,
    screenResolution: componentValue(components, "screen_resolution"),
    pixelRatio: numberValue(componentValue(components, "pixel_ratio")),
    touchSupport: touchValue === null ? null : touchValue === "true",
    pointerCapabilities: componentValue(components, "pointer_capabilities"),
    hardwareConcurrency: numberValue(componentValue(components, "hardware_concurrency")),
    deviceMemoryGb: numberValue(componentValue(components, "device_memory")),
    webglVendor: componentValue(components, "webgl_vendor"),
    webglRenderer: componentValue(components, "webgl_renderer"),
  };
}

export function collectNavigatorComponents(): RawComponent[] {
  const nav = navigator as Navigator & {
    userAgentData?: NavigatorUAData;
    deviceMemory?: number;
  };

  const { family, version } = parseBrowserFamilyVersion(nav.userAgent);
  const clientHints = nav.userAgentData
    ? JSON.stringify({
        brands: nav.userAgentData.brands,
        mobile: nav.userAgentData.mobile,
        platform: nav.userAgentData.platform,
      })
    : null;

  let timezone: string | null = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = null;
  }

  const pointerCapabilities = [
    window.matchMedia?.("(pointer: coarse)").matches ? "coarse" : null,
    window.matchMedia?.("(pointer: fine)").matches ? "fine" : null,
    window.matchMedia?.("(any-pointer: coarse)").matches ? "any-coarse" : null,
  ]
    .filter(Boolean)
    .join(",");

  return [
    { name: "user_agent", rawValue: nav.userAgent ?? null },
    { name: "ua_client_hints", rawValue: clientHints },
    { name: "browser_family", rawValue: family },
    { name: "browser_version", rawValue: version },
    { name: "os_platform", rawValue: nav.userAgentData?.platform ?? nav.platform ?? null },
    {
      name: "languages",
      rawValue: nav.languages ? nav.languages.join(",") : (nav.language ?? null),
    },
    { name: "timezone", rawValue: timezone },
    { name: "screen_resolution", rawValue: `${screen.width}x${screen.height}` },
    { name: "available_screen_resolution", rawValue: `${screen.availWidth}x${screen.availHeight}` },
    { name: "pixel_ratio", rawValue: String(window.devicePixelRatio ?? "") },
    { name: "color_depth", rawValue: String(screen.colorDepth ?? "") },
    {
      name: "hardware_concurrency",
      rawValue: nav.hardwareConcurrency != null ? String(nav.hardwareConcurrency) : null,
    },
    { name: "device_memory", rawValue: nav.deviceMemory != null ? String(nav.deviceMemory) : null },
    {
      name: "touch_support",
      rawValue: String("ontouchstart" in window || (nav.maxTouchPoints ?? 0) > 0),
    },
    { name: "pointer_capabilities", rawValue: pointerCapabilities || null },
  ];
}

const CODEC_PROBES: Array<[string, string]> = [
  ["h264", 'video/mp4; codecs="avc1.42E01E"'],
  ["vp9", 'video/webm; codecs="vp9"'],
  ["av1", 'video/mp4; codecs="av01.0.05M.08"'],
  ["aac", 'audio/mp4; codecs="mp4a.40.2"'],
  ["opus", 'audio/webm; codecs="opus"'],
];

export function collectMediaCodecs(): RawComponent {
  try {
    const video = document.createElement("video");
    const results = CODEC_PROBES.map(
      ([name, mime]) => `${name}:${video.canPlayType(mime) || "no"}`,
    );
    return { name: "media_codecs", rawValue: results.join(",") };
  } catch {
    return { name: "media_codecs", rawValue: null };
  }
}

const CSS_FEATURE_PROBES = [
  "display: grid",
  "display: flex",
  "backdrop-filter: blur(1px)",
  "color: color(display-p3 1 1 1)",
  "container-type: inline-size",
  "accent-color: red",
  "aspect-ratio: 1/1",
];

export function collectCssFeatures(): RawComponent {
  try {
    if (!window.CSS?.supports) return { name: "css_features_hash", rawValue: null };
    const results = CSS_FEATURE_PROBES.map((decl) => {
      const [prop, value] = decl.split(":").map((s) => s.trim());
      return `${prop}:${window.CSS.supports(prop ?? "", value ?? "") ? 1 : 0}`;
    });
    return { name: "css_features_hash", rawValue: results.join(",") };
  } catch {
    return { name: "css_features_hash", rawValue: null };
  }
}

const FONT_CANDIDATES = [
  "Arial",
  "Verdana",
  "Times New Roman",
  "Courier New",
  "Georgia",
  "Comic Sans MS",
  "Impact",
  "Trebuchet MS",
  "Segoe UI",
  "Helvetica Neue",
  "Roboto",
  "Noto Sans",
];
const BASELINE_FONTS = ["monospace", "sans-serif", "serif"];

export function collectFonts(): RawComponent {
  try {
    const testString = "mmmmmmmmmmlli";
    const testSize = "72px";
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return { name: "fonts_hash", rawValue: null };

    const baselineWidths = new Map<string, number>();
    for (const base of BASELINE_FONTS) {
      ctx.font = `${testSize} ${base}`;
      baselineWidths.set(base, ctx.measureText(testString).width);
    }

    const available = FONT_CANDIDATES.filter((font) =>
      BASELINE_FONTS.some((base) => {
        ctx.font = `${testSize} '${font}', ${base}`;
        return ctx.measureText(testString).width !== baselineWidths.get(base);
      }),
    );

    return { name: "fonts_hash", rawValue: available.sort().join(",") };
  } catch {
    return { name: "fonts_hash", rawValue: null };
  }
}
